import { ArrowUp, Github, Instagram, Linkedin, Mail, Youtube, } from 'lucide-react';
import { profileData } from '@/data/profile';
import { SiLeetcode } from 'react-icons/si';

export default function Footer() {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const quickLinks = [
    { label: 'About', id: 'about' },
    { label: 'Skills', id: 'skills' },
    { label: 'Projects', id: 'projects' },
    { label: 'Experience', id: 'experience' },
    { label: 'Contact', id: 'contact' }
  ];

  const socialLinks = [
    { icon: Linkedin, href: profileData.contacts.linkedin, label: 'LinkedIn' },
    { icon: Github, href: profileData.contacts.github, label: 'GitHub' },
    { icon: Youtube, href: profileData.contacts.youtube, label: 'Youtube' },
    { icon: SiLeetcode, href: profileData.contacts.leetcode, label: 'Leetcode' },
    { icon: Instagram, href: profileData.contacts.instagram, label: 'Instagram' },
    { icon: Mail, href: `mailto:${profileData.contacts.email}`, label: 'Email' },
  ];

  return (
    <footer className="relative py-16 px-4 border-t border-glass-border/30">
      <div className="max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <h3 className="text-2xl font-bold text-foreground mb-4">{profileData.name}</h3>
            <p className="text-muted-foreground leading-relaxed mb-6 max-w-md">
              Building innovative digital solutions that make a difference. 
              Always learning, always growing, always coding.
            </p>
            <div className="flex md:space-x-4 space-x-2">
              {socialLinks.map((social) => {
                const IconComponent = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    aria-label={social.label}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 glass-card flex items-center justify-center text-muted-foreground hover:text-primary transition-colors duration-300"
                  >
                    <IconComponent className="h-5 w-5" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-foreground mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className="text-muted-foreground hover:text-primary transition-colors duration-200"
                    aria-label="Social Links"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-semibold text-foreground mb-4">Get in Touch</h4>
            <div className="space-y-2 text-muted-foreground">
              <p>{profileData.contacts.email}</p>
              <p>{profileData.contacts.phone}</p>
              <p>Mumbra, Maharashtra</p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row items-center justify-between pt-8 border-t border-glass-border/30">
          <div className="text-muted-foreground text-sm mb-4 md:mb-0">
            © {new Date().getFullYear()} {profileData.name}. All rights reserved.
          </div>
          
          <div className="flex items-center space-x-4">
            <span className="text-sm text-muted-foreground">Made with ❤️ and coffee</span>
          </div>
        </div>
      </div>

      {/* Back to Top Floating Button */}
      <button
        onClick={scrollToTop}
        className="fixed bottom-8 right-8 w-12 h-12 glass-card flex items-center justify-center text-primary hover:scale-110 transition-transform duration-300 z-40"
        aria-label="Back to top"
      >
        <ArrowUp className="h-5 w-5" />
      </button>
    </footer>
  );
}